import React, { useEffect, useState } from "react";
import { parse, format } from "date-fns";
import PrimaryButton from "../buttons/PrimaryButton";
import Pagination from "../pagination/Pagination";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import axios from "axios";
import Cookies from "js-cookie";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import Modal from "../modal/Modal";
import confirmIcon from "../../assets/img/iconamoon--shield-yes-fill.svg";
import PageSizeSelector from "../pagination/PageSizeSelector";
import usePaginatedData from "../pagination/hooks/usePaginatedData";

const CoordinatorIndexedRecords = ({
  activeTab,
  searchQuery,
  startDate,
  endDate,
}) => {
  // Configuration for endpoints and custom transform functions
  const endpoints = {
    "indexed-records": `/api/index/indexed-records/archives`,
    "yellow-forms": `/api/yellow-forms/archives`
  };

  // Custom transform functions for indexed records
  const customTransformFunctions = {
    'indexed-records': (data) => {
      if (!Array.isArray(data)) {
        console.error("transformIndexedRecords: data is not an array", data);
        return [];
      }
      return data.map((item) => ({
        id: item.id,
        province: item.site?.site?.province || "-",
        district: item.site?.site?.district || "-",
        sector: item.site?.site?.sector || "-",
        bookName: item.book?.name || "-",
        bookType: item.book?.bookType ? item.book.bookType.charAt(0).toUpperCase() + item.book.bookType.slice(1).toLowerCase() : "-",
        count: item.count || 0,
        clerkName: item.assignedToClerk?.name || item.clerk?.name || "-",
        status: item.status || "PENDING",
        recordedAt: item.createdAt
          ? format(new Date(item.createdAt), "dd-MM-yyyy")
          : "-",
        createdAt: item.createdAt || null,
      }));
    },
    'yellow-forms': (data) => {
      if (!Array.isArray(data)) {
        console.error("transformYellowForms: data is not an array", data);
        return [];
      }
      return data.map((item) => ({
        id: item.id,
        province: item.site?.site?.province || "-",
        district: item.site?.site?.district || "-",
        sector: item.site?.site?.sector || "-",
        yellowFormCount: item.yellowFormCount || 0,
        clerkName: item.assignedToClerk?.name || item.clerk?.name || "-",
        status: item.status || "PENDING",
        recordedAt: item.createdAt
          ? format(new Date(item.createdAt), "dd-MM-yyyy")
          : "-",
        createdAt: item.createdAt || null,
      }));
    }
  };

  // Use the pagination hook with custom transform functions
  const {
    data: filteredData,
    pagination,
    loading,
    error: fetchError,
    goToPage,
    changePageSize,
    refresh
  } = usePaginatedData({
    activeTab,
    searchQuery,
    startDate,
    endDate,
    endpoints,
    transformFunctions: customTransformFunctions,
    initialPageSize: 10
  });

  const [error, setError] = useState(null);
  const [showActionConfirm, setShowActionConfirm] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [recordsToProcess, setRecordsToProcess] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const role = Cookies.get("role");

  useEffect(() => {
    let timer;
    if (showSuccess) {
      timer = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccess]);

  // Reset selected records when page changes or data updates
  useEffect(() => {
    setSelectedRecords([]);
    setIsAllSelected(false);
  }, [pagination.currentPage, activeTab, filteredData]);

  const getRecordDetails = (recordId) => {
    return filteredData.find((item) => item.id === recordId) || {};
  };

  const actionDisabled = (status) =>
    status === "APPROVED" || status === "REJECTED";

  const handleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setIsAllSelected(isChecked);
    if (isChecked) {
      setSelectedRecords(
        filteredData
          .filter((item) => !actionDisabled(item.status))
          .map((item) => item.id)
      );
    } else {
      setSelectedRecords([]);
    }
  };

  const handleSelectRecord = (e, recordId) => {
    const isChecked = e.target.checked;
    setSelectedRecords((prev) =>
      isChecked ? [...prev, recordId] : prev.filter((id) => id !== recordId)
    );
    
    const availableForSelection = filteredData.filter((item) => !actionDisabled(item.status));
    const newSelectedCount = isChecked 
      ? selectedRecords.length + 1 
      : selectedRecords.length - 1;
    
    setIsAllSelected(newSelectedCount === availableForSelection.length);
  };

  const handleActionConfirm = (recordIds, action) => {
    setRecordsToProcess(Array.isArray(recordIds) ? recordIds : [recordIds]);
    setActionType(action);
    setShowActionConfirm(true);
  };

  const handleProcessRecords = async () => {
    if (!recordsToProcess.length) return;
    setSubmitting(true);
    const isYellowForm = activeTab === "yellow-forms";
    const isMultiple = recordsToProcess.length > 1;
    
    try {
      const token = Cookies.get("token");
      let endpoint;
      let payload;

      if (actionType === "RESET") {
        if (recordsToProcess.length > 1) {
          throw new Error("Reset action can only be performed on a single record");
        }
        endpoint = isYellowForm 
          ? `/api/yellow-forms/archives/${recordsToProcess[0]}/reset`
          : `/api/index/indexed-records/archives/${recordsToProcess[0]}/reset`;
        payload = isYellowForm ? { formType: "INDEXED" } : {};
      } else {
        endpoint = isYellowForm
          ? actionType === "APPROVED"
            ? `/api/yellow-forms/archives/approve/multiple`
            : `/api/yellow-forms/archives/reject/multiple`
          : actionType === "APPROVED"
          ? `/api/index/indexed-records/archives/approve/multiple`
          : `/api/index/indexed-records/archives/reject/multiple`;
        payload = isYellowForm
          ? { recordIds: recordsToProcess, formType: "INDEXED" }
          : { recordIds: recordsToProcess };
      }

      const response = await axios.post(
        `${baseUrl}${endpoint}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        setSuccessMessage(
          actionType === "RESET"
            ? "Record has been successfully reset to pending!"
            : isMultiple
              ? `Records have been successfully ${
                  actionType === "APPROVED" ? "approved" : "declined"
                }!`
              : `Record has been successfully ${
                  actionType === "APPROVED" ? "approved" : "declined"
                }!`
        );
        setShowActionConfirm(false);
        setShowSuccess(true);
        setSelectedRecords([]);
        setIsAllSelected(false);
        refresh();
      }
    } catch (error) {
      setError(
        `Error ${
          actionType === "APPROVED" ? "approving" : actionType === "REJECTED" ? "declining" : "resetting"
        } record(s): ${error.message}`
      );
      console.error("Processing error:", error);
    } finally {
      setSubmitting(false);
      setRecordsToProcess([]);
    }
  };

  const handlePageChange = (newPage) => {
    goToPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value);
    changePageSize(newPageSize);
  };

  // Table configuration
  const indexedRecordsColumns = [
    "Province",
    "District",
    "Sector",
    "Book Name",
    "Book Type",
    "Indexed Records",
    "Data Entry Clerk",
    "Status",
    "Recorded At",
    ...(role === "PROJECT_COORDINATOR" ? ["Action"] : []),
  ];

  const yellowFormsColumns = [
    "Province",
    "District",
    "Sector",
    "Yellow Form Count",
    "Data Entry Clerk",
    "Status",
    "Recorded At",
    ...(role === "PROJECT_COORDINATOR" ? ["Action"] : []),
  ];

  const formatDate = (dateString) => {
    const parsedData = parse(dateString, "dd-MM-yyyy", new Date());
    return format(parsedData, "dd MMM yyyy");
  };

  const formatStatus = (status) => {
    if (!status) return "";
    if (status.toUpperCase() === "REJECTED") return "Declined";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const getStatusClass = (status) => {
    const statusMap = {
      APPROVED: "status-approved",
      REJECTED: "status-declined",
      PENDING: "status-pending",
    };
    return `status ${statusMap[status]}`;
  };

  return (
    <>
      {/* Page Size Selector */}
      <div className="table-header-controls">
        <PageSizeSelector
          value={pagination.itemsPerPage}
          onChange={handlePageSizeChange}
          disabled={loading}
        />
        
        {/* Show error message if fetch failed */}
        {fetchError && (
          <div className="error-message">
            <span style={{ color: "#E20000", marginRight: "10px" }}>
              Error: {fetchError}
            </span>
            <button 
              onClick={refresh} 
              className="retry-button"
              disabled={loading}
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {selectedRecords.length > 0 && (
        <div
          className="table-controls"
          style={{ marginBottom: "20px", marginRight: "20px" }}
        >
          <PrimaryButton
            text={`Approve ${selectedRecords.length} Record${
              selectedRecords.length > 1 ? "s" : ""
            }`}
            icon="dashicons:yes"
            onClick={() => handleActionConfirm(selectedRecords, "APPROVED")}
            disabled={submitting}
            style={{ backgroundColor: "#078ECE", color: "#FFFFFF" }}
          />
          <PrimaryButton
            text={`Decline ${selectedRecords.length} Record${
              selectedRecords.length > 1 ? "s" : ""
            }`}
            icon="dashicons:no"
            onClick={() => handleActionConfirm(selectedRecords, "REJECTED")}
            disabled={submitting}
            style={{
              backgroundColor: "#E2000033",
              color: "#E20000",
              marginLeft: "20px",
              marginTop: "10px",
            }}
          />
        </div>
      )}

      <table>
        <thead>
          <tr style={{ textTransform: "uppercase" }}>
            {role === "PROJECT_COORDINATOR" && (
              <th>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={loading}
                />
              </th>
            )}

            {(activeTab === "indexed-records"
              ? indexedRecordsColumns
              : yellowFormsColumns
            ).map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, index) => (
              <tr key={index}>
                {role === "PROJECT_COORDINATOR" && (
                  <td>
                    <Skeleton width={20} height={20} />
                  </td>
                )}
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={150} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={80} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
                <td>
                  <Skeleton width={80} />
                </td>
                <td>
                  <Skeleton width={100} />
                </td>
              </tr>
            ))
          ) : filteredData.length === 0 ? (
            <tr style={{ boxShadow: "none" }}>
              <td
                colSpan="11"
                style={{ textAlign: "center", color: "#9B9B9B" }}
              >
                No data found
              </td>
            </tr>
          ) : (
            filteredData.map((item, index) => (
              <tr key={item.id}>
                {role === "PROJECT_COORDINATOR" && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRecords.includes(item.id)}
                      onChange={(e) => handleSelectRecord(e, item.id)}
                      disabled={actionDisabled(item.status)}
                    />
                  </td>
                )}
                <td>{item.province}</td>
                <td>{item.district}</td>
                <td>{item.sector}</td>
                {activeTab === "indexed-records" ? (
                  <>
                    <td>
                      {item.bookName
                        ? item.bookName === "Special_category"
                          ? "Special Category"
                          : item.bookName
                        : "-"}
                    </td>
                    <td>{item.bookType}</td>
                    <td>{item.count}</td>
                  </>
                ) : (
                  <td>{item.yellowFormCount}</td>
                )}
                <td>{item.clerkName}</td>
                <td>
                  <span className={getStatusClass(item.status)}>
                    {formatStatus(item.status)}
                  </span>
                </td>
                <td>{formatDate(item.recordedAt)}</td>
                {role === "PROJECT_COORDINATOR" && (
                  <td>
                    <div className="action-buttons">
                      {item.status === "PENDING" ? (
                        <>
                          <PrimaryButton
                            text="Approve"
                            icon="dashicons:yes"
                            onClick={() => handleActionConfirm(item.id, "APPROVED")}
                            disabled={submitting}
                          />
                          <PrimaryButton
                            text="Decline"
                            onClick={() => handleActionConfirm(item.id, "REJECTED")}
                            disabled={submitting}
                          />
                        </>
                      ) : (
                        <PrimaryButton
                          text={
                            submitting && recordsToProcess.includes(item.id) ? (
                              <AiOutlineLoading3Quarters className="loader" />
                            ) : (
                              "Reset"
                            )
                          }
                          icon="material-symbols:restart-alt"
                          onClick={() => handleActionConfirm(item.id, "RESET")}
                          disabled={submitting && recordsToProcess.includes(item.id)}
                          style={{
                            width: "100%",
                            justifyContent: "center",
                            backgroundColor:
                              submitting && recordsToProcess.includes(item.id)
                                ? "#cccccc"
                                : "#E6F4EA",
                            border: "1px solid rgb(0, 112, 17)",
                            color:
                              submitting && recordsToProcess.includes(item.id)
                                ? "#666666"
                                : "#007011",
                            minWidth: 110,
                            cursor:
                              submitting && recordsToProcess.includes(item.id)
                                ? "not-allowed"
                                : "pointer",
                          }}
                        />
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Modals */}
      <Modal
        show={showActionConfirm}
        onClose={() => setShowActionConfirm(false)}
      >
        <div className="content confirmation">
          <h2>
            {actionType === "APPROVED"
              ? "Confirm Approval"
              : actionType === "REJECTED"
              ? "Confirm Rejection"
              : "Confirm Reset"}
          </h2>
          <div className="text">
            {recordsToProcess.length > 1 ? (
              <>
                Are you sure you want to{" "}
                {actionType === "APPROVED" ? "approve" : actionType === "REJECTED" ? "decline" : "reset"} the
                following {recordsToProcess.length} records?
                <ul style={{ listStyleType: "none", padding: "10px 0" }}>
                  {recordsToProcess.map((id) => {
                    const record = getRecordDetails(id);
                    return (
                      <li key={id}>
                        {activeTab === "indexed-records"
                          ? `${record.count} indexed records for ${record.bookName} in ${record.sector}, ${record.district}, ${record.province}`
                          : `${record.yellowFormCount} yellow forms in ${record.sector}, ${record.district}, ${record.province}`}
                      </li>
                    );
                  })}
                </ul>
              </>
            ) : recordsToProcess.length === 1 ? (
              <>
                Are you sure you want to{" "}
                {actionType === "APPROVED" ? "approve" : actionType === "REJECTED" ? "decline" : "reset"}{" "}
                {activeTab === "indexed-records"
                  ? `${
                      getRecordDetails(recordsToProcess[0]).count
                    } indexed records for ${
                      getRecordDetails(recordsToProcess[0]).bookName
                    } in ${getRecordDetails(recordsToProcess[0]).sector}, ${
                      getRecordDetails(recordsToProcess[0]).district
                    }, ${getRecordDetails(recordsToProcess[0]).province}`
                  : `${
                      getRecordDetails(recordsToProcess[0]).yellowFormCount
                    } yellow forms in ${
                      getRecordDetails(recordsToProcess[0]).sector
                    }, ${getRecordDetails(recordsToProcess[0]).district}, ${
                      getRecordDetails(recordsToProcess[0]).province
                    }`}
                ?
              </>
            ) : (
              "No records selected for processing."
            )}
          </div>
          <div className="button-group">
            <PrimaryButton
              text={
                submitting ? (
                  <AiOutlineLoading3Quarters className="loader" />
                ) : (
                  "Yes"
                )
              }
              onClick={handleProcessRecords}
              disabled={submitting}
            />
            <PrimaryButton
              text="No"
              onClick={() => setShowActionConfirm(false)}
              disabled={submitting}
            />
          </div>
        </div>
      </Modal>

      <Modal show={showSuccess} onClose={() => setShowSuccess(false)}>
        <div className="content confirmation">
          <div className="icon-container">
            <img src={confirmIcon} alt="confirm" width="57" height="57" />
          </div>
          <div className="text">{successMessage}</div>
        </div>
      </Modal>

      {/* Pagination */}
      {!loading && pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.currentPage}
          totalItems={pagination.totalItems}
          entriesPerPage={pagination.itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}
    </>
  );
};

export default CoordinatorIndexedRecords;
